// dist/recorder only registers rrweb; posthog-recorder also registers initSessionRecording, which stops the lazy load
import 'posthog-js/dist/posthog-recorder';

import posthog, { type CaptureResult } from 'posthog-js';

import { clientCookies } from '@/shared/constants/constants';
import { getCookie, setCookie } from '@/shared/utils/cookie';
import { isSuppressedErrorMessage } from '@/shared/utils/error';

import type {
  PostHogConfig,
  StubbedPosthog,
  StubbedPosthogEvent
} from './types';

export const setupAPICredentials = (): void => {
  const apiHost = import.meta.env.VITE_POSTHOG_API_HOST;
  const apiHostCookie = getCookie(clientCookies.POSTHOG_API_HOST);

  if (apiHost && apiHost !== apiHostCookie) {
    setCookie(clientCookies.POSTHOG_API_HOST, apiHost, 365);
  }

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  const apiKeyCookie = getCookie(clientCookies.POSTHOG_API_KEY);

  if (apiKey && apiKey !== apiKeyCookie) {
    setCookie(clientCookies.POSTHOG_API_KEY, apiKey, 365);
  }
};

const piiParams = [
  'user_email',
  'email',
  'user_name',
  'first_name',
  'last_name',
  'phone',
  'token'
];

const urlPropertyPatterns = ['url', 'href', 'host', 'pathname', 'referrer'];

function stripPiiParams(value: string): string {
  try {
    const u = new URL(value);
    let changed = false;
    for (const param of piiParams) {
      if (u.searchParams.has(param)) {
        u.searchParams.delete(param);
        changed = true;
      }
    }
    return changed ? u.toString() : value;
  } catch {
    return value;
  }
}

// An apostrophe is allowed inside a URL (o'brien@x.com) but not as its last character, so a closing quote is left out.
const urlInTextPattern = /https?:\/\/[^\s"<>)]*[^\s"'<>).,;:!?]/g;

const stripPiiParamsFromText = (text: string): string =>
  text.replace(urlInTextPattern, stripPiiParams);

function isUrlProperty(key: string): boolean {
  const lower = key.toLowerCase();
  return urlPropertyPatterns.some((p) => lower.includes(p));
}

function redactPiiFromProperties(
  properties: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!properties || typeof properties !== 'object') return properties;
  const result: Record<string, unknown> = {};
  for (const key in properties) {
    if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
    const value = properties[key];
    if (typeof value === 'string' && isUrlProperty(key)) {
      result[key] = stripPiiParams(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

const redactPiiFromException = (event: CaptureResult): void => {
  if (event.event !== '$exception' || !event.properties) return;
  const { $exception_list: exceptionList, $exception_message: message } =
    event.properties;
  if (Array.isArray(exceptionList)) {
    for (const exception of exceptionList) {
      if (typeof exception?.value === 'string') {
        exception.value = stripPiiParamsFromText(exception.value);
      }
    }
  }
  if (typeof message === 'string') {
    event.properties.$exception_message = stripPiiParamsFromText(message);
  }
};

const isSuppressedException = (event: CaptureResult): boolean => {
  if (event.event !== '$exception') return false;
  const exceptionList: unknown = event.properties?.$exception_list;
  if (!Array.isArray(exceptionList)) return false;
  return exceptionList.some((exception: { value?: string }) =>
    isSuppressedErrorMessage(exception?.value)
  );
};

export const initializeSDK = (config?: Partial<PostHogConfig>): void => {
  const apiKey = config?.apiKey ?? import.meta.env.VITE_POSTHOG_API_KEY;
  const apiHost = config?.apiHost ?? import.meta.env.VITE_POSTHOG_API_HOST;

  posthog.init(apiKey, {
    api_host: apiHost,
    ui_host: config?.uiHost ?? 'https://eu.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: false,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false
    },
    persistence_name: config?.persistenceName ?? 'shopify_theme',
    before_send: (event) => {
      if (!event || isSuppressedException(event)) return null;
      event.properties = redactPiiFromProperties(
        event.properties
      ) as typeof event.properties;
      redactPiiFromException(event);
      event.$set = redactPiiFromProperties(event.$set) as typeof event.$set;
      event.$set_once = redactPiiFromProperties(
        event.$set_once
      ) as typeof event.$set_once;
      return event;
    }
  });

  // Make PostHog globally available
  window.posthog = posthog;
};

export const stubPostHog = (): void => {
  const events: StubbedPosthogEvent[] = [];
  window.posthog = {
    events,
    capture: (...args: StubbedPosthogEvent) => events.push(args)
  } as unknown as typeof posthog;
};

export const getStubbedEvents = (): StubbedPosthogEvent[] => {
  const stubbedPosthog = window.posthog as unknown as StubbedPosthog;
  return stubbedPosthog?.events ?? [];
};

export const replayStubbedEvents = (events: StubbedPosthogEvent[]): void => {
  events.forEach((args) => {
    window.posthog?.capture(...args);
  });
};

export const isPostHogEnabled = (): boolean => {
  if (window.Shopify?.designMode) return false;
  return !!window.Hobbii?.flags?.enableBetaProductAnalytics;
};
